import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Fade,
} from '@mui/material';
import {
  StorefrontRounded,
  CheckCircleOutline,
  ErrorOutline,
  ReceiptLong,
} from '@mui/icons-material';
import { BillingService } from '../../services/billingService.js';

export const ReceiptPage = () => {
  const { receiptId } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const data = await BillingService.fetchReceipt(receiptId);
        setReceipt(data);
      } catch (err) {
        setError(err.message || 'Receipt not found');
      } finally {
        setLoading(false);
      }
    };

    if (receiptId) fetchReceipt();
  }, [receiptId]);

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const PageWrapper = ({ children }) => (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'rgba(69, 39, 160, 0.04)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        p: 2,
        pt: { xs: 2, sm: 4 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 440,
          bgcolor: 'rgba(255, 255, 255, 0.72)',
          borderRadius: '20px',
          boxShadow: '0 4px 24px rgba(69, 39, 160, 0.08)',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );

  // ── Loading ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <PageWrapper>
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: 'primary.main', mb: 2 }} />
          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Loading receipt...
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  // ── Error ───────────────────────────────────────────────────────
  if (error) {
    return (
      <PageWrapper>
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <ErrorOutline sx={{ fontSize: 56, color: '#FF3B30', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Receipt Not Found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {error}
          </Typography>
        </Box>
      </PageWrapper>
    );
  }

  // ── Receipt ─────────────────────────────────────────────────────
  return (
    <PageWrapper>
      <Fade in timeout={500}>
        <Box>
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #4527A0 0%, #7E57C2 100%)',
              p: 3,
              color: 'rgba(255, 255, 255, 0.72)',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <ReceiptLong sx={{ fontSize: 28 }} />
            </Box>
            <Typography variant="subtitle2" sx={{ opacity: 0.8, fontSize: '0.72rem', letterSpacing: 1.5 }}>
              PAYMENT RECEIPT
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
              {receipt?.outlet_name || receipt?.outlet_info?.outlet_name || 'Restaurant'}
            </Typography>
          </Box>

          {/* Paid Badge */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: -1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'rgba(52, 199, 89, 0.08)',
                color: '#2E7D32',
                borderRadius: '20px',
                px: 2,
                py: 0.5,
                fontWeight: 700,
                fontSize: '0.78rem',
                boxShadow: '0 2px 8px rgba(46, 125, 50, 0.12)',
              }}
            >
              <CheckCircleOutline sx={{ fontSize: 16 }} />
              PAID
            </Box>
          </Box>

          {/* Order Info */}
          <Box sx={{ p: 3, pt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                bgcolor: 'rgba(69, 39, 160, 0.04)',
                borderRadius: '14px',
                p: 2,
                mb: 2.5,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                  Order #
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {receipt?.order_num || '-'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                  Paid at
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                  {formatDate(receipt?.paid_at)}
                </Typography>
              </Box>
            </Box>

            {/* Items */}
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.68rem', letterSpacing: 1.5 }}
            >
              ITEMS
            </Typography>

            <Box sx={{ mt: 1 }}>
              {(receipt?.items || []).map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    borderBottom:
                      idx < (receipt?.items?.length || 0) - 1
                        ? '1px solid #E0E0E3'
                        : 'none',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.86rem' }}>
                      {item.item_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>
                      ₹{(item.unit_price || 0).toFixed(2)} × {item.quantity}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    ₹{(item.amount || 0).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Totals */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Subtotal</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ₹{(receipt?.subtotal || 0).toFixed(2)}
                </Typography>
              </Box>
              {(receipt?.tax || 0) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Tax</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ₹{(receipt?.tax || 0).toFixed(2)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 0.5,
                  pt: 1,
                  borderTop: '2px solid #4527A0',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Total Paid</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  ₹{(receipt?.total || 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {/* Transaction Details */}
            {(receipt?.transaction_id || receipt?.payment_method) && (
              <Box
                sx={{
                  mt: 2.5,
                  bgcolor: 'rgba(69, 39, 160, 0.04)',
                  borderRadius: '14px',
                  p: 2,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.62rem', letterSpacing: 1.5 }}
                >
                  TRANSACTION DETAILS
                </Typography>
                {receipt?.transaction_id && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Transaction ID</Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.72rem' }}
                    >
                      {receipt.transaction_id}
                    </Typography>
                  </Box>
                )}
                {receipt?.payment_method && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Method</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                      {receipt.payment_method}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ px: 3, pb: 3, pt: 0, textAlign: 'center' }}>
            <Divider sx={{ mb: 2 }} />
            <StorefrontRounded sx={{ fontSize: 20, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', display: 'block' }}>
              Thank you for your visit!
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#B0B0B5', fontSize: '0.6rem', mt: 0.5, display: 'block' }}
            >
              Powered by Karobar
            </Typography>
          </Box>
        </Box>
      </Fade>
    </PageWrapper>
  );
};
